<!-- Base.Checkbox.vue (client-lms) -->
<template>
  <label class="lms_checkbox" :class="{ 'lms_checkbox_disabled': disabled, 'lms_checkbox_circle': variant === 'circle' }">
    <input
      ref="inputRef"
      type="checkbox"
      :value="value"
      :checked="isChecked"
      @change="onChange"
      :disabled="disabled"
    />
    <span class="lms_checkbox_mark" :class="{ 'indeterminate': indeterminate }"></span>
    <span v-if="label || $slots.default" class="lms_checkbox_label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";

const model = defineModel<string | boolean | null>();

const props = defineProps({
  value: { type: String, default: "" },
  label: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  checked: { type: Boolean, default: undefined },
  indeterminate: { type: Boolean, default: false },
  variant: { type: String, default: "square" }, // 'square' or 'circle'
});

const emit = defineEmits<{
  (e: "change", checked: boolean): void;
}>();

const inputRef = ref<HTMLInputElement>();

watch(() => props.indeterminate, (val) => {
  if (inputRef.value) inputRef.value.indeterminate = val;
}, { immediate: true });

const isChecked = computed(() => {
  if (props.checked !== undefined) return props.checked;
  if (typeof model.value === "boolean") return model.value;
  if (!model.value) return false;
  return String(model.value).includes(props.value);
});

const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("change", target.checked);
  if (typeof model.value === "boolean" || props.checked !== undefined) {
    model.value = target.checked;
  } else {
    model.value = target.checked ? props.value : null;
  }

  // 부모가 변경을 거부해 isChecked 가 그대로면(예: 시험 응시 정답 개수 초과 선택),
  // 바인딩 값이 안 바뀌어 Vue 가 DOM 을 다시 칠하지 않는다 → 클릭으로 켜진 native 체크가 남는다.
  // 실제 상태(isChecked)로 되돌려 '선택 불가'가 화면에도 반영되게 한다. 정상 흐름에선 no-op.
  nextTick(() => {
    if (inputRef.value && inputRef.value.checked !== isChecked.value) {
      inputRef.value.checked = isChecked.value;
    }
  });
};
</script>

<style scoped>
.lms_checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.lms_checkbox input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.lms_checkbox_mark {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid var(--lms-on-surface-low-var);
  border-radius: 2px;
  background-color: var(--lms-surface, #FFFFFF);
  transition: all 0.15s;
  flex-shrink: 0;
}

.lms_checkbox_circle .lms_checkbox_mark {
  border-radius: 50%;
}

.lms_checkbox input:checked + .lms_checkbox_mark {
  background-color: var(--lms-primary-400);
  border-color: var(--lms-primary-400);
}

.lms_checkbox input:checked + .lms_checkbox_mark::after {
  content: "";
  display: block;
  width: 5px;
  height: 9px;
  border: solid #FFFFFF;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-top: -1px;
}

/* Indeterminate ( - ) */
.lms_checkbox_mark.indeterminate {
  background-color: var(--lms-primary-300, #7D90F9);
  border-color: var(--lms-primary-300, #7D90F9);
}

.lms_checkbox_mark.indeterminate::after {
  content: "";
  display: block;
  width: 10px;
  height: 2px;
  background-color: #FFFFFF;
  border: none;
  transform: none;
  margin-top: 0;
}

.lms_checkbox:hover .lms_checkbox_mark {
  border-color: var(--lms-outline-black, #AAABAB);
}

.lms_checkbox input:checked:hover + .lms_checkbox_mark {
  border-color: var(--lms-primary-400, #5C74F6);
}

.lms_checkbox_disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.lms_checkbox_label {
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
}
</style>
